import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { SiteFooter, SiteHeader } from "../components/BrandLogo";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function AuthPage({ mode }) {
  const isSignup = mode === "signup";
  const p = isSignup ? "signup" : "login";
  const { user, authenticate } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rawNext = params.get("next") || "";
  const next = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/dashboard";
  const carry = params.toString() ? `?${params.toString()}` : "";
  const [form, setForm] = useState({ name: "", email: params.get("email") || "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await authenticate(isSignup ? "register" : "login", isSignup ? form : { email: form.email, password: form.password });
      navigate(next);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, type = "text", extra = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={key} className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</Label>
      <Input id={key} type={type} data-testid={`${p}-${key}-input`} className="h-12 rounded-xl bg-white" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} {...extra} />
    </div>
  );

  return (
    <div className="confetti-bg-white font-manrope flex min-h-screen flex-col text-slate-900">
      <SiteHeader>
        <Link to="/" data-testid="auth-home-link" className="text-sm font-medium text-slate-600 transition-colors hover:text-blue-700">Home</Link>
      </SiteHeader>
      <div className="flex flex-1 items-start justify-center px-4 pb-16 pt-10 sm:items-center">
        <div className="rise w-full max-w-md rounded-2xl border border-slate-200 bg-white/90 p-8 shadow-sm backdrop-blur-xl sm:p-10">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-blue-700">{isSignup ? "Become a host" : "Welcome back"}</p>
          <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-slate-900">{isSignup ? "Start your party" : "Host login"}</h1>
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid={`${p}-form`}>
            {isSignup && field("name", "Your name", "text", { placeholder: "Alex Morgan" })}
            {field("email", "Email", "email", { required: true, placeholder: "you@example.com", autoComplete: "email" })}
            {field("password", "Password", "password", { required: true, minLength: isSignup ? 6 : undefined, autoComplete: isSignup ? "new-password" : "current-password" })}
            {!isSignup && (
              <div className="-mt-2 text-right">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm font-semibold text-blue-700 hover:text-blue-800">Forgot your password?</Link>
              </div>
            )}
            {error && <p data-testid="auth-error" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}
            <Button type="submit" disabled={busy} data-testid={`${p}-submit-button`} className="h-12 w-full rounded-lg text-base font-semibold">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : isSignup ? "Create account" : "Log in"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-600">
            {isSignup ? "Already hosting?" : "New here?"}{" "}
            <Link to={`${isSignup ? "/login" : "/signup"}${carry}`} data-testid="auth-switch-link" className="font-bold text-blue-700 hover:text-blue-800">
              {isSignup ? "Log in" : "Create an account"}
            </Link>
          </p>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
